import { useRef, useEffect } from "react";
import LogoBlack from "../assets/logo/Logo_Black.svg";
import B3Logo from "../assets/logo/b3_bahnbau.svg";
import B3HLogo from "../assets/logo/b3h.png";
import RMGLogo from "../assets/logo/rmg.png";
import EBBLogo from "../assets/logo/ebb.png";
import { servicesDataEn, servicesDataAr } from "../data/servicesData";
import { projectsDataEn, projectsDataAr } from "../data/projectsData";
import SafetyImage from "../assets/safety.avif";
import { useLanguage } from "../LanguageContext";

export default function HomeView({ navigate }) {
  const { isAr } = useLanguage();
  const t = (en, ar) => (isAr ? ar : en);

  const servicesData = isAr ? servicesDataAr : servicesDataEn;
  const projectsData = isAr ? projectsDataAr : projectsDataEn;

  const stats = [
    { value: "450+", label: t("Projects Completed", "مشروع مكتمل") },
    { value: "1000+", label: t("Km of Cable Working", "كم من الكابل") },
    { value: "100+", label: t("Employees", "موظف") },
    { value: "150+", label: t("Logistics Support", "دعم لوجستي") },
  ];

  const scrollRef = useRef(null);
  const scrollDir = useRef(1);
  const lastDir = useRef(1);
  const isAnimating = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (!el || el.scrollWidth === 0 || isAnimating.current || scrollDir.current === 0) return;
      const half = el.scrollWidth / 2;
      if (scrollDir.current === 1) {
        if (isAr) {
          el.scrollLeft -= 1;
          if (el.scrollLeft <= -half) el.scrollLeft += half;
        } else {
          el.scrollLeft += 1;
          if (el.scrollLeft >= half) el.scrollLeft -= half;
        }
      } else {
        if (isAr) {
          if (el.scrollLeft >= -1) el.scrollLeft = -half;
          else el.scrollLeft += 1;
        } else {
          if (el.scrollLeft <= 1) el.scrollLeft = half;
          else el.scrollLeft -= 1;
        }
      }
    }, 20);
    return () => clearInterval(interval);
  }, [isAr]);

  const handleArrow = (dir) => {
    const el = scrollRef.current;
    if (!el || isAnimating.current) return;

    const logicalDir = isAr ? -dir : dir;
    scrollDir.current = logicalDir;
    lastDir.current = logicalDir;

    const half = el.scrollWidth / 2;
    if (isAr) {
      if (dir === 1 && el.scrollLeft > -400) el.scrollLeft -= half;
    } else {
      if (dir === -1 && el.scrollLeft < 400) el.scrollLeft += half;
    }

    const start = el.scrollLeft;
    const target = start + dir * 360;
    const duration = 500;
    const startTime = performance.now();
    isAnimating.current = true;
    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      el.scrollLeft = start + (target - start) * ease;
      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        if (isAr) {
          if (el.scrollLeft <= -half) el.scrollLeft += half;
          if (el.scrollLeft > 0) el.scrollLeft = 0;
        } else {
          if (el.scrollLeft >= half) el.scrollLeft -= half;
          if (el.scrollLeft < 0) el.scrollLeft = 0;
        }
        isAnimating.current = false;
      }
    };
    requestAnimationFrame(animate);
  };

  return (
    <div className="view-enter">
      {/* Hero */}
      <div className="relative w-full h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gray-900">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover object-center">
            <source src="/hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 hero-gradient opacity-60" />
        </div>
        <div className="relative z-10 flex flex-col items-center text-center px-5 sm:px-6 max-w-5xl mx-auto w-full pt-20 sm:pt-0">
          <span className="inline-flex items-center gap-2 py-1.5 sm:py-2 px-4 sm:px-5 rounded-full bg-white/10 text-white border border-white/30 text-xs font-semibold tracking-widest mb-5 sm:mb-8 backdrop-blur-md uppercase">
            {t("Est. 2024", "تأسست 2024")}
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-3 sm:mb-6 leading-tight font-display">
            {t("Building Smarter Railway Infrastructure", "بناء بنية تحتية ذكية للسكك الحديدية")}
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/80 mb-6 sm:mb-10 max-w-xl md:max-w-2xl font-light leading-relaxed">
            {t(
              "Axion delivers advanced railway infrastructure and precise project execution across Saudi Arabia and Europe.",
              "تقدم أكسيون بنية تحتية متقدمة للسكك الحديدية وتنفيذاً دقيقاً للمشاريع في المملكة العربية السعودية وأوروبا."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full px-2 sm:px-0">
            <button
              onClick={() => navigate("services")}
              className="bg-primary-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base hover:bg-primary-700 hover:-translate-y-0.5 transition-all duration-300 shadow-lg flex items-center justify-center gap-2 group w-full sm:w-auto"
            >
              {t("Explore Our Services", "استكشف خدماتنا")}
              <i className={`ph-bold ${isAr ? "ph-arrow-left" : "ph-arrow-right"} text-lg group-hover:${isAr ? "-translate-x-1" : "translate-x-1"} transition-transform`} />
            </button>
            <button
              onClick={() => navigate("contact")}
              className="bg-transparent border-2 border-white/50 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base hover:bg-white hover:text-primary-700 transition-all duration-300 w-full sm:w-auto"
            >
              {t("Contact Us", "تواصل معنا")}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-primary-600 text-white py-12 sm:py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(#ffffff 2px, transparent 2px)", backgroundSize: "30px 30px" }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-center text-sm font-bold tracking-[0.2em] text-white/70 uppercase mb-12 font-display">
            {t("Trusted by Rail Operators Nationwide", "موثوق به من قبل مشغلي السكك الحديدية في جميع أنحاء المملكة")}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 text-center divide-y sm:divide-y-0 sm:divide-x divide-white/20">
            {stats.map(({ value, label }) => (
              <div key={label} className="px-2 sm:px-4 py-4 md:py-0">
                <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-1 sm:mb-2 tracking-tighter font-display text-white">
                  {value}
                </h3>
                <p className="text-white/80 font-medium tracking-wide uppercase text-sm">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Services Section */}
      <div className="bg-gray-50 py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-primary-700 font-bold tracking-wider uppercase mb-3 text-sm font-display text-center">
            {t("What We Do", "ما نقوم به")}
          </h3>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6 sm:mb-8 md:mb-10 font-display text-center">
            {t("Our Services", "خدماتنا")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {Object.entries(servicesData).slice(0, 3).map(([key, data]) => (
              <ServiceCard key={key} serviceKey={key} data={data} navigate={navigate} isAr={isAr} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {Object.entries(servicesData).slice(3, 5).map(([key, data]) => (
              <ServiceCard key={key} serviceKey={key} data={data} navigate={navigate} isAr={isAr} />
            ))}
          </div>
        </div>
      </div>

      {/* Featured Projects Section */}
      <div className="bg-white py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-10">
          <h3 className="text-primary-700 font-bold tracking-wider uppercase mb-3 text-sm font-display text-center">
            {t("Our Projects", "مشاريعنا")}
          </h3>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 font-display text-center">
            {t("Featured Projects", "المشاريع المميزة")}
          </h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto">
            {t(
              "Complex railway projects across Europe with high safety and quality standards.",
              "مشاريع سكك حديدية معقدة في جميع أنحاء أوروبا بمعايير سلامة وجودة عالية."
            )}
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative">
            <button
              onClick={() => handleArrow(-1)}
              className="absolute left-2 sm:-left-5 top-1/2 -translate-y-1/2 z-20 bg-white w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg border-2 border-gray-200 flex items-center justify-center text-primary-700 hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all duration-300"
            >
              <i className="ph-bold ph-caret-left text-lg" />
            </button>
            <button
              onClick={() => handleArrow(1)}
              className="absolute right-2 sm:-right-5 top-1/2 -translate-y-1/2 z-20 bg-white w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg border-2 border-gray-200 flex items-center justify-center text-primary-700 hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all duration-300"
            >
              <i className="ph-bold ph-caret-right text-lg" />
            </button>
            <div
              className="projects-scroll-track"
              ref={scrollRef}
              onMouseEnter={() => { scrollDir.current = 0; }}
              onMouseLeave={() => { scrollDir.current = lastDir.current || 1; }}
            >
              {Object.entries(projectsData).map(([key, data]) => (
                <ProjectCard key={key} projectKey={key} data={data} navigate={navigate} isAr={isAr} />
              ))}
              {Object.entries(projectsData).map(([key, data]) => (
                <ProjectCard key={`dup-${key}`} projectKey={key} data={data} navigate={navigate} isAr={isAr} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-gray-50 py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div>
            <h3 className="text-primary-700 font-bold tracking-wider uppercase mb-3 text-sm font-display">
              {t("Your Advantage", "ميزتك")}
            </h3>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 sm:mb-6 font-display">
              {t("Why Choose Axion Railway", "لماذا تختار أكسيون للسكك الحديدية")}
            </h2>
            <p className="text-gray-600 mb-10">
              {t(
                "Axion Railway is your reliable partner for demanding railway infrastructure projects in Europe.",
                "أكسيون للسكك الحديدية هي شريكك الموثوق لمشاريع البنية التحتية الصعبة للسكك الحديدية في أوروبا."
              )}
            </p>
            <ul className="space-y-6">
              {[
                {
                  icon: "ph-bold ph-shield-check",
                  title: t("Safety Focus", "التركيز على السلامة"),
                  desc: t("Zero-accident culture with clear and certified processes.", "ثقافة الصفر في الحوادث مع عمليات واضحة ومعتمدة."),
                },
                {
                  icon: "ph-bold ph-lightbulb",
                  title: t("Proven Expertise", "خبرة مثبتة"),
                  desc: t("Experienced teams for complex railway environments.", "فرق ذات خبرة في بيئات السكك الحديدية المعقدة."),
                },
                {
                  icon: "ph-bold ph-calendar-check",
                  title: t("On-Time Delivery", "التسليم في الوقت المحدد"),
                  desc: t("Structured planning and construction for reliable deadlines.", "تخطيط وبناء منظم لمواعيد نهائية موثوقة."),
                },
              ].map(({ icon, title, desc }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="bg-primary-600 text-white rounded-xl p-3">
                    <i className={`${icon} text-2xl`} />
                  </span>
                  <div>
                    <h4 className="font-bold text-lg text-gray-900">{title}</h4>
                    <p className="text-gray-600 text-sm">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative flex justify-center mt-8 md:mt-0">
            <div className="relative max-w-md w-full">
              <img src={SafetyImage} alt={t("Axion Railway Team", "فريق أكسيون للسكك الحديدية")} className="rounded-2xl shadow-xl w-full aspect-square object-cover" />
              <div className="absolute -bottom-4 -right-2 md:-right-6 bg-axion-base p-4 md:p-6 shadow-2xl flex flex-col items-center justify-center min-w-[120px] md:min-w-[140px] border border-white/10 rounded-xl">
                <span className="text-5xl md:text-6xl font-black text-axion-gold font-display leading-none mb-1">0</span>
                <span className="text-white font-semibold tracking-wider uppercase text-[10px] md:text-xs text-center leading-tight">
                  {isAr ? <>حوادث<br />السلامة</> : <>Safety<br />Incidents</>}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partnership Section */}
      <div className="bg-white py-12 sm:py-16 md:py-20 px-4 sm:px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-primary-700 font-bold tracking-wider uppercase mb-3 text-sm font-display">
            {t("Our Partnership", "شراكتنا")}
          </h3>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-8 sm:mb-10 md:mb-12 font-display">
            {t("Strong Partners for European Railways", "شركاء أقوياء للسكك الحديدية الأوروبية")}
          </h2>

          {/* Axion Railway — main company */}
          <div className="flex justify-center mb-6">
            <div className="flex flex-col items-center group">
              <div className="h-32 w-64 p-6 bg-white rounded-2xl shadow-soft border border-gray-100 flex items-center justify-center mb-4 group-hover:shadow-medium transition-all duration-300">
                <img src={LogoBlack} alt="Axion Railway" className="w-full h-full object-contain" />
              </div>
              <p className="font-bold text-gray-900 text-lg">{t("Axion Railway", "أكسيون للسكك الحديدية")}</p>
            </div>
          </div>

          {/* Handshake divider */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 sm:w-32 bg-gray-200" />
            <i className="ph-bold ph-handshake text-4xl sm:text-5xl text-gray-300" />
            <div className="h-px w-16 sm:w-32 bg-gray-200" />
          </div>

          {/* 4 partner logos */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            {[
              { src: B3HLogo, alt: "B3 Holding GmbH", label: t("B3 Holding GmbH", "B3 هولدينج") },
              { src: B3Logo, alt: "B3 Bahnbau GmbH", label: t("B3 Bahnbau GmbH", "B3 باهنباو") },
              { src: RMGLogo, alt: "Rhein Mosel Gleisbau GmbH", label: t("Rhein Mosel Gleisbau GmbH", "راين موزيل غلايسباو") },
              { src: EBBLogo, alt: "EBB Emser Bahnbau GmbH", label: t("EBB Emser Bahnbau GmbH", "EBB إيمزر باهنباو") },
            ].map(({ src, alt, label }) => (
              <div key={alt} className="flex flex-col items-center group">
                <div className="h-28 w-52 p-5 bg-white rounded-2xl shadow-soft border border-gray-100 flex items-center justify-center mb-3 group-hover:shadow-medium transition-all duration-300">
                  <img src={src} alt={alt} className="w-full h-full object-contain" />
                </div>
                <p className="font-semibold text-gray-800 text-sm text-center max-w-[13rem]">{label}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
            {t(
              "Through our strategic partnerships, B3 Holding GmbH, B3 Bahnbau GmbH, Rhein Mosel Gleisbau GmbH, EBB Emser Bahnbau GmbH and Axion Railway combine decades of expertise in civil engineering, track construction, and advanced railway technology to deliver comprehensive, turnkey solutions for complex infrastructure projects.",
              "من خلال شراكاتنا الاستراتيجية، تجمع B3 هولدينج وB3 باهنباو وراين موزيل غلايسباو وEBB إيمزر باهنباو وأكسيون للسكك الحديدية عقوداً من الخبرة في الهندسة المدنية وإنشاء المسارات وتكنولوجيا السكك الحديدية المتقدمة لتقديم حلول شاملة ومتكاملة لمشاريع البنية التحتية المعقدة."
            )}
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 sm:py-16 px-4 sm:px-6" style={{ backgroundColor: "rgb(17, 60, 111)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6 font-display text-white">
            {t("Ready to start your next railway project?", "هل أنت مستعد لبدء مشروعك القادم للسكك الحديدية؟")}
          </h2>
          <p className="text-white/90 mb-8 sm:mb-10 text-sm sm:text-base md:text-lg font-light">
            {t(
              "Contact our experts today for a consultation on cable laying, LST, civil engineering, and surveying technology.",
              "تواصل مع خبرائنا اليوم للحصول على استشارة حول مد الكابلات ونظم السلامة والهندسة المدنية وتقنية المساحة."
            )}
          </p>
          <button
            onClick={() => navigate("contact")}
            className="bg-white text-primary-700 px-8 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg hover:bg-primary-700 hover:text-white shadow-button transition-all transform hover:-translate-y-1 inline-block w-full sm:w-auto"
          >
            {t("Contact Us Today", "تواصل معنا اليوم")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ serviceKey, data, navigate, isAr }) {
  const t = (en, ar) => (isAr ? ar : en);
  return (
    <div
      className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col cursor-pointer group interactive-card"
      onClick={() => navigate("service-detail", serviceKey)}
    >
      <div className="h-48 w-full overflow-hidden">
        <img src={data.image} alt={data.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h4 className="font-bold text-lg mb-2 text-gray-900">{data.title}</h4>
        <p className="text-gray-600 text-sm flex-1">{data.shortDesc}</p>
        <button className="mt-4 text-primary-600 font-semibold flex items-center gap-1">
          {t("View Details", "عرض التفاصيل")}
          <i className={`ph-bold ${isAr ? "ph-arrow-left" : "ph-arrow-right"} text-base arrow-icon`} />
        </button>
      </div>
    </div>
  );
}

function ProjectCard({ projectKey, data, navigate, isAr }) {
  const t = (en, ar) => (isAr ? ar : en);
  return (
    <div
      className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col border border-gray-100 min-w-[320px] max-w-[320px] cursor-pointer group interactive-card"
      onClick={() => navigate("project-detail", projectKey)}
    >
      <div className="h-44 w-full overflow-hidden">
        <img src={data.image} alt={data.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <span className="text-xs font-bold uppercase text-primary-600 mb-2 tracking-wider">{data.tagLabel}</span>
        <h4 className="font-bold text-lg mb-1 text-gray-900">{data.title}</h4>
        <p className="text-gray-600 text-sm flex-1">{data.subtitle}</p>
        <button className="mt-4 text-primary-600 font-semibold flex items-center gap-1">
          {t("View Details", "عرض التفاصيل")}
          <i className={`ph-bold ${isAr ? "ph-arrow-left" : "ph-arrow-right"} text-base arrow-icon`} />
        </button>
      </div>
    </div>
  );
}
